'use client';

import { useEffect, useRef, useState } from 'react';

import { NliApiError, useAppDisplayRequestErrors } from '@hooks/useAppDisplayRequestErrors';

import { useHateoasContext } from '@context/HateoasContext';

import { getAccessElements } from '@utils/functions';
import myFetch from '@utils/myFetch';

type AiWaitingModule = '' | 'dt' | 'opportunity' | 'interviewPreparation' | 'profile-matching';
type AiWaitingLink = { rel?: string; href?: string; method?: string };
type AiWaitingSentence = { _id: string; _links?: AiWaitingLink[]; [key: string]: unknown };
type AiWaitingSettings = { random?: boolean; loopPeriod?: number } | null;

const useAiWaiting = (show: boolean, module: AiWaitingModule, data?: AiWaitingSettings) => {
    const loopRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const [sentences, setSentences] = useState<AiWaitingSentence[]>([]);
    const indexRef = useRef(0);
    const displayRequestErrors = useAppDisplayRequestErrors();
    const [mounted, setMounted] = useState(false);
    const [index, setIndex] = useState(0);
    const [settings, setSettings] = useState<AiWaitingSettings>(data || null);
    const { getRel } = useHateoasContext();

    useEffect(() => {
        if (!data) {
            getAiWaitingSettings();
        }
    }, [data]);

    useEffect(() => {
        if (settings === null) {
            return;
        }
        if (loopRef.current) {
            clearInterval(loopRef.current);
        }

        if (show) {
            const getWaitingSentences = async (module: AiWaitingModule) => {
                const link = getRel('waiting-sentence:read');
                const method = (link?.method || 'GET').toLowerCase();
                const path = link?.href;
        
                if (!path) {
                    return;
                }
                try {
                    const waitingSentencesList = (await myFetch[method as 'get'](`${path}?module=${module}`)) as AiWaitingSentence[];
                    setSentences(waitingSentencesList);

                    if (settings.random && waitingSentencesList?.length > 1) {
                        indexRef.current = Math.floor(Math.random() * waitingSentencesList.length);
                        setIndex(indexRef.current);
                    }
                    setMounted(true);
                    if (waitingSentencesList?.length > 1 && settings.loopPeriod) {
                        loopRef.current = setInterval(() => {
                            if (settings) {
                                const array = waitingSentencesList.filter((_, i) => i !== indexRef.current);
                                const randomIndex = Math.floor(Math.random() * array.length);
                                indexRef.current = array[randomIndex] ? waitingSentencesList.indexOf(array[randomIndex]) : 0;
                            } else {
                                if (indexRef.current < waitingSentencesList.length - 1) {
                                    indexRef.current += 1;
                                } else {
                                    indexRef.current = 0;
                                }
                            }
                            setMounted(false);
                        }, settings.loopPeriod * 1000);
                    }
                } catch (err: unknown) {
                    displayRequestErrors(err as NliApiError);
                }
            };
            getWaitingSentences(module);
        }

        return () => {
            if (loopRef.current) {
                clearInterval(loopRef.current);
            }
        };
    }, [show, module, settings, getRel]);

    useEffect(() => {
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };

        if (show) {
            window.addEventListener('beforeunload', onBeforeUnload);
        } else {
            window.removeEventListener('beforeunload', onBeforeUnload);
        }
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [show]);

    const getAiWaitingSettings = async () => {
        const settings = (await myFetch.get('settings/public')) as { aiWaitingModal?: AiWaitingSettings };
        setSettings(settings.aiWaitingModal);
    };

    const onLike = async (id: string, _links: AiWaitingLink[]) => {
        const { method, path } = getAccessElements(_links, 'waiting-sentence:likes');
        if (!path) {
            return;
        }
        try {
            const response = await myFetch[method](path);
            const newSentence = response.data;
            const newSentences = sentences.map((s) => (s._id === id ? { ...newSentence } : s));
            setSentences(newSentences);
        } catch (err: unknown) {
            displayRequestErrors(err as NliApiError);
        }
    };

    const handleExited = () => {
        setMounted(true);
        setIndex(indexRef.current);
    };

    return {
        sentences,
        onLike,
        handleExited,
        mounted,
        index,
    };
};

export default useAiWaiting;
